import {
  SUPERNOVA_CHAIN_ID,
  SUPERNOVA_COLLECTION_ADDRESS,
  SUPERNOVA_STAKING_ADDRESS,
  SUPERNOVA_TOKENS,
} from "./supernovaContracts";
import { SUPERNOVA_ERROR, SupernovaChainError } from "./supernovaChainError";
import { LOCK_PRESETS } from "../../config/supernovaXp";
import {
  stakeAmountArg,
  stakeDurationSeconds,
  stakeIdArg,
  stakeTokenIdArg,
} from "./supernovaWriteArgs";

/**
 * supernovaSignatureRequest — the exact description of every transaction this
 * app will ever ask a member to sign.
 *
 * Each builder turns what someone picked on screen into a plain, JSON-safe
 * object stating, in the contract's own units, what the transaction does. That
 * object is three things at once:
 *
 *   1. THE THING THAT GETS LOGGED, verbatim, immediately before the wallet
 *      prompt opens — the member's own record of what they were asked for.
 *   2. THE THING THE GUARDS CHECK AGAINST. supernovaWriteGuards compares the
 *      calldata supernovaChain is about to send against this description, field
 *      by field, and refuses to let the two diverge. That is what makes the log
 *      trustworthy rather than decorative: it cannot describe one transaction
 *      while a different one goes to the wallet.
 *   3. THE UNITS A HUMAN REVIEWS. The two that have ever been wrong are
 *      `durationSeconds` (the member picks DAYS) and `amount` (a raw ERC-1155
 *      count, never an 18-decimal amount). Both are mapped by supernovaWriteArgs
 *      — the same mapping the write path uses, not a second copy of it.
 *
 * WHAT THIS MODULE DOES NOT DO, AND MUST NOT LEARN TO DO:
 *   - construct, encode, simulate or submit a transaction
 *   - request a signature, an approval, or any delegated permission
 *   - touch a wallet, a provider, a client, or viem
 *
 * It describes; supernovaChain is the only module that talks to a wallet.
 *
 * PURE. No React, no network, no chain. Importable by a test without a wallet.
 */

/**
 * The custody and risk sentence both confirm steps carry, per the issue's
 * compliance check. Three facts, no promises: the member's own wallet is the
 * only thing that signs, the exit is theirs to take under the lock terms they
 * already saw quoted, and the strategies these slots unlock carry risk.
 *
 * ORDER IS DELIBERATE (ARC-38's framing, extended to the confirm steps by
 * ARC-39). Custody leads and the risk clause closes the same sentence — the same
 * order as the Supernova and Strategy Automation banners, so a member does not
 * meet one framing on the tab and the opposite one at the signature. NOTHING WAS
 * DROPPED IN THE REORDER: leveraged instruments, involves risk, losing the
 * capital you allocate, your own wallet signs every transaction, ARC Educational
 * holds no keys / NFTs / funds, and unstake at any time on the lock terms shown
 * are all still here, verbatim — and still in ONE element, so the risk clause
 * cannot drift smaller or dimmer than the custody copy it qualifies.
 */
export const CONFIRM_RISK_SENTENCE =
  "Your own wallet signs every transaction — ARC Educational never holds your " +
  "keys, your NFTs or your funds — and you can unstake at any time on the lock " +
  "terms shown above. Authorizing automated trading of leveraged instruments " +
  "involves risk, including losing the capital you allocate to it.";

const UNMAPPABLE_STAKE_MESSAGE =
  "We couldn't turn those choices into a request the staking contract accepts, " +
  "so nothing was submitted. Check the type, the quantity and the lock, and try " +
  "again.";
const UNMAPPABLE_LOT_MESSAGE =
  "We couldn't identify which stake to close, so nothing was submitted. Reload " +
  "the tab and try again.";

const tokenName = (tokenId) => {
  const token = SUPERNOVA_TOKENS.find((t) => t.id === tokenId);
  return token ? token.name : null;
};

const lockLabel = (lockDays) => {
  const preset = LOCK_PRESETS.find((p) => p.days === lockDays);
  return preset ? preset.label : null;
};

/* Contract arguments are BigInts. They are carried as decimal strings so the
   payload survives JSON.stringify — a console log that throws on a BigInt is a
   review artifact nobody gets to read. */
const asDecimal = (value) => (value === null ? null : String(value));

/**
 * What a stake would ask the member to sign, in the contract's units.
 *
 * @param {{wallet: string, tokenId: number, qty: number, lockDays: number}} request
 * @returns {Object} plain, JSON-safe
 * @throws {SupernovaChainError} WRITE_FAILED when the choices can't be mapped
 */
export function buildStakeSignaturePayload(request = {}) {
  const { wallet, tokenId, qty, lockDays } = request;

  const tokenIdArg = stakeTokenIdArg(tokenId);
  const amountArg = stakeAmountArg(qty);
  const durationArg = stakeDurationSeconds(lockDays);
  if (tokenIdArg === null || amountArg === null || durationArg === null) {
    throw new SupernovaChainError(
      SUPERNOVA_ERROR.WRITE_FAILED,
      UNMAPPABLE_STAKE_MESSAGE
    );
  }

  return {
    kind: "stake",
    chainId: SUPERNOVA_CHAIN_ID,
    /* The member's own address — the only thing that could ever sign this. */
    wallet: wallet || null,
    contract: SUPERNOVA_STAKING_ADDRESS,
    contractFunction: "stake(address,uint256,uint256,uint256)",
    /* What the member picked, and what it maps to. Both, side by side, because
       the review is checking the mapping and not the choice. */
    chose: {
      tokenId,
      tokenName: tokenName(tokenId),
      qty,
      lockDays,
      lockLabel: lockLabel(lockDays),
    },
    args: {
      token: SUPERNOVA_COLLECTION_ADDRESS,
      tokenId: asDecimal(tokenIdArg),
      amount: asDecimal(amountArg),
      durationSeconds: asDecimal(durationArg),
    },
  };
}

/**
 * What closing a lot would ask the member to sign. `emergency` picks the
 * pre-unlock exit — decided by the surface that told the member which exit they
 * were taking, never inferred here.
 *
 * @param {{wallet: string, lotId: string, stakeId?: string|number,
 *          emergency?: boolean, matured?: boolean}} request
 * @returns {Object} plain, JSON-safe
 * @throws {SupernovaChainError} WRITE_FAILED when the lot can't be identified
 */
export function buildUnstakeSignaturePayload(request = {}) {
  const { wallet, lotId, stakeId, emergency, matured } = request;

  const stakeIdArgument = stakeIdArg(stakeId === undefined ? lotId : stakeId);
  if (stakeIdArgument === null) {
    throw new SupernovaChainError(SUPERNOVA_ERROR.WRITE_FAILED, UNMAPPABLE_LOT_MESSAGE);
  }

  const early = Boolean(emergency);
  return {
    kind: early ? "emergencyUnstake" : "unstake",
    chainId: SUPERNOVA_CHAIN_ID,
    wallet: wallet || null,
    contract: SUPERNOVA_STAKING_ADDRESS,
    contractFunction: early ? "emergencyUnstake(uint256)" : "unstake(uint256)",
    chose: {
      lotId: lotId === undefined ? null : lotId,
      /* The contract closes whole lots; there is no partial exit to describe. */
      wholeLot: true,
      exit: early ? "early" : "matured",
      matured: matured === undefined ? !early : Boolean(matured),
    },
    args: { stakeId: asDecimal(stakeIdArgument) },
  };
}

/**
 * What the one-time ERC-1155 operator approval would ask for. Permission only —
 * it moves nothing by itself, and it is revocable from the member's wallet.
 *
 * @param {{wallet: string}} request
 * @returns {Object} plain, JSON-safe
 */
export function buildApprovalSignaturePayload(request = {}) {
  return {
    kind: "setApprovalForAll",
    chainId: SUPERNOVA_CHAIN_ID,
    wallet: request.wallet || null,
    contract: SUPERNOVA_COLLECTION_ADDRESS,
    contractFunction: "setApprovalForAll(address,bool)",
    chose: { grantingTo: "the Supernova staking contract", revocable: true },
    args: { operator: SUPERNOVA_STAKING_ADDRESS, approved: true },
  };
}

/** The tag every signature-request log line carries, so it is greppable. */
export const SIGNATURE_REQUESTED = "signature-requested";

/**
 * Log the exact payload immediately before the member's wallet is asked for a
 * signature.
 *
 * This started life as the artifact of the pre-signing safety review, and it
 * stays now that signing is live — for a better reason than it existed for
 * originally. When a member says "I signed something and it did the wrong
 * thing", this line is the record of precisely what the app put in front of
 * them, in the contract's own units, from their own browser console. It is
 * `console.info` rather than a debug channel for exactly that reason.
 *
 * It logs a DESCRIPTION, never a key, a seed or a signature — there is nothing
 * secret in a payload, which is the point of the payload being the thing we log.
 *
 * @param {Object} payload from one of the builders above
 * @returns {Object} the payload, unchanged, so callers can chain
 */
export function logSignatureRequest(payload) {
  if (typeof console !== "undefined" && typeof console.info === "function") {
    // eslint-disable-next-line no-console
    console.info(
      `[supernova] ${SIGNATURE_REQUESTED}: this is exactly what your wallet is ` +
        "about to be asked to sign. Nothing is submitted until you sign it.",
      payload
    );
  }
  return payload;
}
